<script lang="ts" setup>
import { computed, ref, watch, useSlots, toRefs } from "vue";
import { useElementSize, useMagicKeys, useScroll } from "@vueuse/core";
import Overlay from "../Overlay/Overlay.vue";
import Icon from "../Icon/Icon.vue";
import Title from "../Title/Title.vue";

const props = defineProps<{
  isVisible: boolean;
  title: string;
  hasCancelButton?: boolean;
  hasError?: boolean;
  increaseMaxWidth?: boolean;
}>();

const { hasCancelButton } = toRefs(props);

const emit = defineEmits<{
  (e: "cancel"): void;
}>();

const modalMain = ref<HTMLDivElement | null>(null);
const modalContent = ref<HTMLDivElement | null>(null);

const { height: mainHeight } = useElementSize(modalMain);
const { height: contentHeight } = useElementSize(modalContent);

const { arrivedState } = useScroll(modalMain);

const showScrollFade = computed(() => {
  return mainHeight.value < contentHeight.value && !arrivedState.bottom;
});

const keys = useMagicKeys();
// @ts-expect-error: TODO: This new error showed up after updating @vue/tsconfig. Examine if this is really an issue (or `?` or `!` can be applied easily)
watch(keys.Escape, (stroke) => {
  if (stroke && hasCancelButton.value) emit("cancel");
});

const slots = useSlots();
const hasActions = computed(() => !!slots.actions);
</script>

<template>
  <Overlay :is-visible="isVisible">
    <div
      class="modal"
      :data-scroll="showScrollFade ? 'on' : 'off'"
      :data-increased-max-width="increaseMaxWidth || undefined"
      data-testid="modal"
    >
      <header
        class="modal--header"
        :data-has-error="hasError"
      >
        <Title
          tag="h2"
          size="s"
          :text="title"
        />
        <button
          v-if="hasCancelButton"
          type="button"
          :aria-label="$t('shared.action.close')"
          @click="$emit('cancel')"
        >
          <Icon icon="close" />
        </button>
      </header>
      <div
        ref="modalMain"
        class="modal--main"
        :data-has-footer="hasActions"
      >
        <div
          ref="modalContent"
          class="modal--content"
        >
          <slot name="main"></slot>
        </div>
      </div>
      <div class="modal--bottom">
        <footer
          v-if="hasActions"
          class="modal--footer"
        >
          <slot name="actions"></slot>
        </footer>
      </div>
    </div>
  </Overlay>
</template>

<style scoped>
@import "Modal.css";
</style>
